import { useCallback, useEffect, useRef } from "react";
import { mappingApi } from "@services/mappingApi";
import { notificationApi } from "@services/notificationApi";
import { ApiError } from "@utils/httpClient";
import type {
  JobViewModel,
  MappingJobNotification,
  MappingJobProgressNotification,
} from "@models/job";

export const useJobNotifications = (
  onJobUpdate: (job: JobViewModel) => void,
  onJobProgress?: (progress: MappingJobProgressNotification) => void,
): {
  ensureConnected: () => Promise<void>;
  subscribe: (jobId: string) => () => void;
} => {
  const unsubscribesRef = useRef<Map<string, () => void>>(new Map());

  const refetchJob = useCallback(
    async (jobId: string) => {
      try {
        const job = await mappingApi.getJob(jobId);
        onJobUpdate(job);
      } catch (error) {
        const message =
          error instanceof ApiError
            ? error.message
            : error instanceof Error
              ? error.message
              : "Failed to fetch job status";
        console.error(`Failed to refetch job ${jobId}: ${message}`);
      }
    },
    [onJobUpdate],
  );

  const ensureConnected = useCallback(async (): Promise<void> => {
    await notificationApi.connect();
  }, []);

  const subscribe = useCallback(
    (jobId: string): (() => void) => {
      const existing = unsubscribesRef.current.get(jobId);
      if (existing) return existing;

      const handleMessage = (message: MappingJobNotification): void => {
        if (message.type === "mapping.job.progress") {
          onJobProgress?.(message);
          return;
        }
        console.debug(`Mapping WS: ${message.type} for job ${jobId}`);
        void refetchJob(jobId);
      };
      const rawUnsub = notificationApi.subscribe(jobId, handleMessage);
      const unsub = (): void => {
        rawUnsub();
        unsubscribesRef.current.delete(jobId);
      };
      unsubscribesRef.current.set(jobId, unsub);
      return unsub;
    },
    [refetchJob, onJobProgress],
  );

  useEffect(() => {
    const currentUnsubs = unsubscribesRef.current;
    return () => {
      for (const unsub of currentUnsubs.values()) {
        unsub();
      }
      currentUnsubs.clear();
    };
  }, []);

  return { ensureConnected, subscribe };
};
